const _excluded = ["editorHeight", "placeholder", "initialValue", "error", "helperText", "className", "isReadOnly", "editorRef", "onChange"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Typography } from "../Typography";
import { Quill, replaceNbsp } from "../../patches/PatchedQuill";
import styles from "./RichTextEditor.module.scss";
import { makeParsedRichText, supportedFormats } from "../../utils/richText";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const theme = 'snow';

/** Returns a pure-text string from a quill rich text object. */
export const extractPureTextFromRichText = function extractPureTextFromRichText() {
  let richText = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  const quill = new Quill(document.createElement('div'), {
    modules: {
      toolbar: false
    },
    theme: 'snow'
  });
  applyValueToQuill(quill, richText);
  return quill.getText();
};
function sanitizeDelta(parsed) {
  const delta = parsed.value;
  if (!('ops' in delta)) {
    return delta;
  }

  // This cleans up &nbsp; characters that have already been saved in the database.
  delta.ops.forEach(op => {
    if (op.insert && typeof op.insert === 'string') {
      op.insert = replaceNbsp(op.insert);
    }
  });
  return delta;
}
export const applyValueToQuill = (quill, value) => {
  if (value) {
    if (typeof value === 'object') {
      // We assume it's a rich-text object.
      quill.setContents(sanitizeDelta(value));
    } else {
      try {
        // Maybe its a stringified rich-text object.
        const parsed = JSON.parse(value);
        if (parsed.value) {
          quill.setContents(sanitizeDelta(parsed));
        } else {
          // Sometimes we can parse a string of pure text. For example, when it contains only digits.
          // In that case, we want to assume its pure text.
          quill.setText(value);
        }
      } catch (err) {
        // We assume its pure text.
        quill.setText(value);
      }
    }
  } else {
    quill.setText('');
  }
};
export function RichTextEditor(props) {
  const {
      editorHeight,
      placeholder,
      initialValue = '',
      error,
      helperText,
      className,
      isReadOnly,
      editorRef,
      onChange
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const instance = useRef({
    onChange
  });
  instance.current = {
    onChange
  };
  const rootRef = useRef(null);
  const hasQuill = useRef(false);
  const [editorEl, setEditorEl] = useState(null);
  useEffect(() => {
    if (!hasQuill.current && editorEl) {
      const quill = new Quill(editorEl, {
        modules: {
          toolbar: [[{
            header: [1, 2, false]
          }], ['bold', 'italic', 'underline', 'strike'], [{
            list: 'ordered'
          }, {
            list: 'bullet'
          }, {
            indent: '-1'
          }, {
            indent: '+1'
          }], ['link']]
        },
        placeholder: placeholder || '',
        theme,
        // Important for tooltips not to open beyond the editor bounds and behave
        // well when the editor is in a modal.
        bounds: rootRef.current,
        formats: supportedFormats,
        readOnly: isReadOnly
      });
      hasQuill.current = true;
      if (props['data-testid']) {
        quill.root.setAttribute('data-testid', props['data-testid'] + '-input');
      }
      applyValueToQuill(quill, initialValue);
      quill.on('text-change', () => {
        const newContent = quill.getContents();
        instance.current.onChange && instance.current.onChange(makeParsedRichText(newContent));
      });
    }
  }, [editorEl]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
    className: cn(styles.root, error && styles.error, className),
    ref: rootRef
  }, rest), {}, {
    children: [/*#__PURE__*/_jsx("div", {
      style: {
        height: editorHeight || 200
      },
      ref: el => {
        editorRef && editorRef(el);
        setEditorEl(el);
      }
    }), helperText && /*#__PURE__*/_jsx(Typography, {
      variant: "paragraph3",
      skin: error ? 'error' : 'secondary',
      className: styles.helperText,
      "data-testid": props['data-testid'] && props['data-testid'] + '-helper-text',
      children: helperText
    })]
  }));
}